import type { ReactNode } from 'react'

export interface DataColumn<T> {
  key: string
  header: string
  align?: 'left' | 'right'
  render: (row: T) => ReactNode
}

interface DataTableProps<T> {
  rows: T[]
  columns: DataColumn<T>[]
  emptyTitle?: string
  emptyDescription?: string
  onRowClick?: (row: T) => void
  getRowKey: (row: T, index: number) => string
}

export default function DataTable<T>({
  rows,
  columns,
  emptyTitle = 'No rows',
  emptyDescription = 'There is no data for this selection.',
  onRowClick,
  getRowKey,
}: DataTableProps<T>) {
  if (rows.length === 0) {
    return (
      <div className="flex min-h-48 items-center justify-center p-6 text-center">
        <div>
          <p className="font-semibold text-gray-200">{emptyTitle}</p>
          <p className="mt-1 text-sm text-gray-500">{emptyDescription}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="overflow-auto custom-scrollbar">
      <table className="w-full min-w-[42rem] border-collapse text-sm">
        <thead>
          <tr className="border-b border-white/10 bg-white/[0.025]">
            {columns.map((column) => (
              <th
                key={column.key}
                className={`px-4 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 ${
                  column.align === 'right' ? 'text-right' : 'text-left'
                }`}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {rows.map((row, index) => (
            <tr
              key={getRowKey(row, index)}
              onClick={() => onRowClick?.(row)}
              className={`transition-colors ${onRowClick ? 'cursor-pointer hover:bg-white/[0.055]' : 'hover:bg-white/[0.025]'}`}
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={`px-4 py-3 text-gray-300 ${column.align === 'right' ? 'text-right tabular-nums' : 'text-left'}`}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
